import { jwtDecode } from "jwt-decode";
import { useState } from "react";
import Form from "react-bootstrap/Form";
import { NavLink } from "react-router";
import { getStoredToken } from "../../../../utils";
import { PDLButton, PDLTypography } from "../../dna";
import PasswordInput from "../_passwordInput";
import "./style.scss";

interface RegisterFormProps {
  onSubmit: (email: string, password: string, name: string) => Promise<void>;
  isLoading: boolean;
  error?: string;
}

export default function RegisterForm({
  onSubmit,
  isLoading,
  error,
}: RegisterFormProps) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const token = getStoredToken();

  const { domains } = (token ? jwtDecode(token) : { domains: [] }) as {
    domains: string[];
  };

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    name?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!email) {
      newErrors.email = "O email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Digite um email válido";
    } else if (domains.length > 0) {
      const emailDomain = email.split("@")[1]?.toLowerCase();

      const allowedDomains = domains.map((d) => d.toLowerCase());

      if (!emailDomain || !allowedDomains.includes(emailDomain)) {
        newErrors.email = `O email deve pertencer a um dos domínios: ${allowedDomains.join(", ")}`;
      }
    }
    if (!name) {
      newErrors.name = "O nome é obrigatório";
    }

    if (!password) {
      newErrors.password = "A senha é obrigatória";
    } else if (password.length < 6) {
      newErrors.password = "A senha deve ter no mínimo 6 caracteres";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirme sua senha";
    } else if (confirmPassword !== password) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      await onSubmit(email, password, name);
    }
  };

  return (
    <div className="pdl_component_registerform">
      <Form onSubmit={handleSubmit} className="d-grid gap-5">
        <div className="d-grid gap-1">
          <PDLTypography display size="md" weight="semibold">
            Crie sua conta
          </PDLTypography>
          <PDLTypography size="md">
            Olá! Por favor, insira seus dados para continuar.
          </PDLTypography>
        </div>

        <div className="d-grid gap-4">
          <div className="d-grid gap-3">
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder={
                  domains && domains.length > 0
                    ? "exemplo@" + domains[0]
                    : "seu@email.com"
                }
                isInvalid={!!errors.email}
                disabled={isLoading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <Form.Label>Nome</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Seu nome"
                isInvalid={!!errors.name}
                disabled={isLoading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>
            <PasswordInput
              label="Senha"
              value={password}
              onChange={setPassword}
              placeholder="Mínimo 6 caracteres"
              error={errors.password}
              disabled={isLoading}
            />

            <PasswordInput
              label="Confirmar senha"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Repita sua senha"
              error={errors.confirmPassword}
              disabled={isLoading}
            />
          </div>

          <div className="d-grid gap-3">
            <PDLButton type="submit" variant="primary" size="lg">
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Enviando...
                </>
              ) : (
                "Criar conta"
              )}
            </PDLButton>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-end">
          <NavLink to="/entrar">Já tem uma conta?</NavLink>
        </div>
        {error && <p className="form_error">{error}</p>}
      </Form>
    </div>
  );
}
